'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { BlogPost } from '@/services/blogService';

interface BlogCardProps {
  post: BlogPost;
  formatDate: (dateString: string) => string;
}

export default function BlogCard({ post, formatDate }: BlogCardProps) {
  return (
    <article className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
      {/* Blog Image */}
      <Link 
        href={`/blog/${post.slug}`} 
        className="block"
        aria-label={`View blog post: ${post.title}`}
        prefetch={false}
      >
        <div className="relative h-48 bg-gray-200 cursor-pointer group">
          <div className="absolute top-4 left-4 z-10">
            <span className="px-3 py-1 text-white text-xs font-medium rounded-full" style={{ backgroundColor: '#6B39FF' }}>
              {post.category.name}
            </span>
          </div>
          {post.featuredImage ? (
            <Image
              src={post.featuredImage}
              alt={post.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              unoptimized
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center group-hover:from-blue-200 group-hover:to-indigo-200 transition-colors duration-300">
              <div className="text-center text-gray-500">
                <div className="w-16 h-16 mx-auto mb-2 bg-gray-300 rounded-full group-hover:bg-gray-400 transition-colors duration-300"></div>
                <p className="text-sm">Blog Image</p>
              </div>
            </div>
          )}
          {/* Overlay for better click indication */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300"></div>
        </div>
      </Link>

      {/* Blog Content */}
      <div className="p-6">
        <div className="mb-3">
          <div className="flex items-center text-sm text-gray-500">
            <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">{formatDate(post.publishedAt)}</span>
            <span className="mx-2">•</span>
            <User className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">{post.authorName || 'Anonymous'}</span>
          </div>
        </div>

        <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
          {post.title}
        </h2>

        <p className="text-gray-600 mb-4 line-clamp-3">
          {post.excerpt}
        </p>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Read More Button */}
        <Link
          href={`/blog/${post.slug}`}
          className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
          aria-label={`Read more about ${post.title}`}
          prefetch={false}
        >
          Read More
          <ArrowRight className="w-4 h-4 ml-1" />
        </Link>
      </div>
    </article>
  );
}
